import type {
  AdmissionDTO,
  BookedTripDTO,
  BookingDTO,
  BookingPaymentDTO,
  BookingSearchDTO,
  FulfillmentDTO,
} from '@/types/dto/agent';
import type { Voucher } from '@/types/purchase';
import type { BookingFulfillmentStorageData } from '@/types/storage';
import { areRefundedLike, isAdmissionInactive } from '@/utils/admission';
import { buildPassengerTypeByPassengerId } from '@/utils/changeTrips';
import { isTimestampInPast } from '@/utils/dateTime';

/**
 * A trip should render in booking details if it has at least one admission that
 * isn't EXCHANGED. EXCHANGED admissions were superseded by a change-booking and
 * moved off this trip, so a trip with only EXCHANGED admissions is a leftover
 * that renders an empty O→D header with no leg cards and must drop out. Terminal
 * admissions that still belong to the trip (REFUNDED/RELEASED/CANCELLED) are
 * kept, so a refunded booking still shows its (grayed-out) tickets.
 */
export const tripIsRenderable = (trip: BookedTripDTO): boolean =>
  (trip.bookedOffers ?? [])
    .flatMap((offer) => offer.admissions ?? [])
    .some((admission) => admission.status !== 'EXCHANGED');

export const isTicketBooking = (booking?: BookingDTO | null): boolean => {
  if (!booking) return false;

  return Boolean(booking.bookedTrips?.length);
};

export const isBookingRefunded = (booking?: BookingDTO | null): boolean => {
  const allTripAdmissions = (booking?.bookedTrips ?? []).flatMap(
    (trip) =>
      trip.bookedOffers?.flatMap((offer) => offer.admissions ?? []) ?? []
  );
  return allTripAdmissions.length > 0 && areRefundedLike(allTripAdmissions);
};

export const isTravelPassBooking = (booking?: BookingDTO | null): boolean => {
  if (!booking) return false;

  return Boolean(booking.nonTripOffers?.length);
};

export const buildPassengerTypeByPassengerIdFromBooking = (
  booking?: BookingDTO | null
): Record<string, string> => {
  const result: Record<string, string> = {};

  for (const trip of booking?.bookedTrips ?? []) {
    Object.assign(result, buildPassengerTypeByPassengerId(trip));
  }

  for (const offer of booking?.nonTripOffers ?? []) {
    for (const admission of offer.admissions ?? []) {
      if (isAdmissionInactive(admission)) continue;

      for (const appliedType of admission.appliedPassengerTypes ?? []) {
        const ref = appliedType.passengerRef;
        const label = appliedType.description ?? appliedType.type;

        if (ref && label) {
          result[ref] = label;
        }
      }
    }
  }

  return result;
};

export const getBookingAdmissions = (
  booking?: BookingDTO | null
): Array<AdmissionDTO> => {
  if (!booking?.bookedTrips?.length && !booking?.nonTripOffers?.length)
    return [];
  const { bookedTrips, nonTripOffers } = booking;

  return bookedTrips?.length
    ? bookedTrips.reduce<Array<AdmissionDTO>>(
        (admissions, trip) =>
          [
            ...admissions,
            ...(trip.bookedOffers?.reduce<Array<AdmissionDTO>>(
              (acc, { admissions }) =>
                admissions ? [...acc, ...admissions] : acc,
              []
            ) ?? []),
          ].filter(({ passengerIds }) => passengerIds),
        []
      )
    : (nonTripOffers?.reduce<Array<AdmissionDTO>>(
        (acc, { admissions }) => (admissions ? [...acc, ...admissions] : acc),
        []
      ) ?? []);
};

export const findBookingBookedTripReservationByReservationId = (
  booking: BookingDTO | null,
  reservationId?: string
) => {
  if (!reservationId) return undefined;

  return booking?.bookedTrips
    ?.flatMap((trip) => trip.bookedOffers || [])
    ?.flatMap((offer) => offer.admissions || [])
    ?.flatMap((admission) => admission.reservations || [])
    ?.find((reservation) => reservation.id === reservationId);
};

export const findBookingBookedTripFeesByFeeRefs = (
  booking: BookingDTO | null,
  feeRefs?: string[]
) => {
  if (!feeRefs || !booking?.bookedTrips) return undefined;

  return booking.bookedTrips
    .flatMap((trip) => trip.bookedOffers || [])
    .flatMap((offer) => offer.fees || [])
    .filter((fee) => fee.id && feeRefs.includes(fee.id));
};

export const mapBookingVoucherPaymentsToVouchers = (
  voucherPayments: Array<BookingPaymentDTO>
): Array<Voucher> =>
  voucherPayments
    .map(({ voucherCode, paidAmount }) => {
      if (!(voucherCode && paidAmount?.amount && paidAmount?.currency)) {
        return null;
      }

      return {
        voucherCode,
        amount: {
          amount: paidAmount.amount,
          currency: paidAmount.currency,
        },
      };
    })
    .filter((voucher): voucher is Voucher => !!voucher);

export const orderBookingTripsBasedOnDepartureTime = (
  booking: BookingDTO | null
): BookingDTO | null =>
  booking
    ? {
        ...booking,
        bookedTrips: (booking.bookedTrips ? [...booking.bookedTrips] : []).sort(
          (a, b) =>
            new Date(a.departureTime).getTime() -
            new Date(b.departureTime).getTime()
        ),
      }
    : null;

export const getBookingNonTripOfferFulfillments = (
  booking: BookingDTO | null
): Array<FulfillmentDTO> =>
  (booking?.nonTripOffers ?? []).flatMap((offer) =>
    (offer.admissions ?? []).flatMap(
      (admission) => admission.fulfillments ?? []
    )
  );

const mapFulfillmentToStorageData = (
  fulfillment: FulfillmentDTO,
  isOutBound?: boolean
): Array<BookingFulfillmentStorageData> => {
  if (!fulfillment.id) {
    return [];
  }

  const base = {
    id: fulfillment.id,
    ...(typeof isOutBound === 'boolean' && { isOutBound }),
  };

  if (fulfillment.fulfillmentDocuments?.length) {
    return fulfillment.fulfillmentDocuments
      .filter((document) => document.documentType)
      .map((document) => ({
        ...base,
        downloadLink: document.downloadLink,
        documentType: document.documentType,
        documentFormat: document.documentFormat,
        mediumType: document.mediumType,
      }));
  }

  return [
    {
      ...base,
      downloadLink: fulfillment.downloadLink,
      documentType: fulfillment.documentType,
      documentFormat: fulfillment.documentFormat,
      mediumType: fulfillment.mediumType,
    },
  ];
};

export const isPkpassFulfillment = (
  fulfillment: BookingFulfillmentStorageData
): boolean =>
  fulfillment.documentType === 'PKPASS' ||
  fulfillment.mediumType === 'PKPASS' ||
  fulfillment.documentFormat?.includes('pkpass') === true ||
  fulfillment.downloadLink?.includes('/pkpass/') === true;

export const getBookingFulfillments = (
  booking: BookingDTO | null
): Array<BookingFulfillmentStorageData> => {
  const isTravelPass = isTravelPassBooking(booking);

  if (isTravelPass) {
    return (
      booking?.nonTripOffers?.flatMap(
        (offer) =>
          offer.admissions?.flatMap(
            (admission) =>
              admission.fulfillments?.flatMap((fulfillment) =>
                mapFulfillmentToStorageData(fulfillment)
              ) ?? []
          ) ?? []
      ) ?? []
    );
  }

  return (
    booking?.bookedTrips?.flatMap(
      (trip) =>
        trip.bookedOffers?.flatMap(
          (offer) =>
            offer.admissions?.flatMap(
              (admission) =>
                admission.fulfillments?.flatMap((fulfillment) =>
                  mapFulfillmentToStorageData(fulfillment, trip.isOutbound)
                ) ?? []
            ) ?? []
        ) ?? []
    ) ?? []
  );
};

// The amount charged for the current checkout. Pending admissions carry it in
// provisionalPrice; once the backend confirms them it moves to confirmedPrice,
// so prefer provisionalPrice while it still holds the pending amount.
export const getBookingTotalPrice = (booking: BookingDTO | null) =>
  booking?.provisionalPrice?.amount
    ? booking.provisionalPrice
    : (booking?.confirmedPrice ?? undefined);

const isTicketFulfillmentWithDownloadLink = (
  fulfillment: BookingFulfillmentStorageData
): boolean =>
  Boolean(
    fulfillment.downloadLink &&
      fulfillment.documentType === 'TICKET' &&
      !isPkpassFulfillment(fulfillment)
  );

export const findTicketFulfillmentWithDownloadLink = (
  fulfillments?: Array<BookingFulfillmentStorageData>
) => fulfillments?.find(isTicketFulfillmentWithDownloadLink);

export const hasMultipleTicketFulfillmentsWithDownloadLink = (
  fulfillments?: Array<BookingFulfillmentStorageData>
): boolean => {
  const fulfillmentIds = new Set<string>();

  for (const fulfillment of fulfillments ?? []) {
    if (fulfillment.id && isTicketFulfillmentWithDownloadLink(fulfillment)) {
      fulfillmentIds.add(fulfillment.id);

      if (fulfillmentIds.size > 1) {
        return true;
      }
    }
  }

  return false;
};

export const getPkpassFulfillments = (
  fulfillments?: Array<BookingFulfillmentStorageData>
): Array<BookingFulfillmentStorageData> => {
  const pkpassFulfillments = fulfillments?.filter(isPkpassFulfillment) ?? [];

  if (pkpassFulfillments.length) {
    return pkpassFulfillments;
  }

  const seen = new Set<string>();

  return (
    fulfillments?.filter((fulfillment) => {
      const { id, documentType } = fulfillment;

      if (
        !id ||
        documentType === 'TICKET' ||
        isPkpassFulfillment(fulfillment)
      ) {
        return false;
      }

      if (seen.has(id)) {
        return false;
      }

      seen.add(id);
      return true;
    }) ?? []
  );
};

export const getLegDirection = (
  booking: BookingDTO | null | undefined,
  legId: string | undefined
): 'outbound' | 'inbound' | undefined => {
  if (!booking?.bookedTrips || !legId) return undefined;

  const trip = booking.bookedTrips.find((t) =>
    t.legs?.some((l) => l.id === legId)
  );

  if (!trip) return undefined;

  return trip.isOutbound ? 'outbound' : 'inbound';
};

export const isBookingSearchItemRefunded = (item: BookingSearchDTO) =>
  Boolean(
    item.status && ['REFUNDED', 'RELEASED', 'CANCELLED'].includes(item.status)
  );

export const isTravelPassExpired = (item: BookingSearchDTO) =>
  Boolean(
    item.travelPassDetails?.validUntil &&
      isTimestampInPast(item.travelPassDetails.validUntil, 'Europe/Stockholm')
  );
